/** Client side of bookkeeping: the books dashboard, transaction questions, and the no-sign-in question page. */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileText, HelpCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, api, errorMessage } from "@/lib/api";
import { formatDate, plural } from "@/lib/format";
import { useToast } from "@/lib/use-toast";

type ClientQuestion = {
  id: string;
  txnType: "Purchase" | "Deposit";
  txnDate: string;
  amountCents: number;
  payee: string | null;
  description: string | null;
  bankAccount: string | null;
  status: "open" | "answered";
  answerText: string | null;
  answerAccountQboId: string | null;
  hasReceipt: boolean;
  isCredit: boolean;
};
type Categories = Record<"Purchase" | "Deposit", Array<{ qboId: string; name: string }>>;
type Answer = { answerText?: string; accountQboId?: string };

const select = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm sm:w-auto";

/** One transaction to explain. Works the same signed in or through a link. */
function QuestionCard({ q, categories, onAnswer, onReceipt }: { q: ClientQuestion; categories: Categories; onAnswer: (a: Answer) => Promise<unknown>; onReceipt: (f: File) => Promise<unknown> }) {
  const [text, setText] = useState(q.answerText ?? "");
  const [account, setAccount] = useState(q.answerAccountQboId ?? "");
  const [editing, setEditing] = useState(q.status === "open");
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => onAnswer({ ...(text.trim() ? { answerText: text.trim() } : {}), ...(account ? { accountQboId: account } : {}) }),
    onSuccess: () => setEditing(false),
  });
  const upload = useMutation({ meta: { inlineError: true }, mutationFn: onReceipt });
  const options = categories[q.txnType];
  const picked = options.find((c) => c.qboId === q.answerAccountQboId)?.name;
  return (
    <li className="space-y-3 rounded-xl border border-card-border bg-card p-4 shadow-xs">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-lg font-semibold">{formatCents(q.amountCents)}</span>
        <span className="text-sm">
          {q.txnType === "Purchase" && !q.isCredit ? "paid to" : q.isCredit ? "refunded by" : "received from"} {q.payee ?? "unidentified payee"}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatDate(q.txnDate)}
          {q.bankAccount && ` · ${q.bankAccount}`}
        </span>
      </div>
      {q.description && <p className="text-xs text-muted-foreground">Bank description: {q.description}</p>}
      {!editing ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <span className="flex-1">
            {q.answerText && `“${q.answerText}”`}
            {q.answerText && picked && " · "}
            {picked}
            {q.hasReceipt && " · Receipt attached"}
          </span>
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            Change
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea aria-label="Transaction description" rows={2} maxLength={2000} placeholder={q.txnType === "Purchase" ? "Describe the purpose of this payment, e.g. printer supplies for the office" : "Describe the source of this deposit, e.g. payment from a customer"} value={text} onChange={(e) => setText(e.target.value)} />
          {options.length > 0 && (
            <select aria-label="Category" className={select} value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">Category (optional)</option>
              {options.map((c) => (
                <option key={c.qboId} value={c.qboId}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending || (!text.trim() && !account)}>
              Submit response
            </Button>
            {q.status === "answered" && (
              <Button variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            )}
          </div>
          {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <UploadButton label={q.hasReceipt ? "Replace receipt" : "Attach receipt"} disabled={upload.isPending} onFile={(f) => upload.mutate(f)} />
        {upload.isPending && <span className="text-xs text-muted-foreground">Uploading…</span>}
        {upload.error && <span className="text-sm text-destructive">{errorMessage(upload.error)}</span>}
      </div>
    </li>
  );
}

function QuestionList({ questions, categories, answer, receipt }: { questions: ClientQuestion[]; categories: Categories; answer: (id: string, a: Answer) => Promise<unknown>; receipt: (id: string, f: File) => Promise<unknown> }) {
  const open = questions.filter((q) => q.status === "open").length;
  return (
    <>
      <p className="mb-3 text-sm text-muted-foreground">
        {open ? `${open} of ${plural(questions.length, "transaction")} ${open === 1 ? "requires" : "require"} your review.` : "All transactions have been reviewed."} Please describe each transaction so it can be categorized correctly.
      </p>
      <ul className="space-y-3">
        {questions.map((q) => (
          <QuestionCard key={q.id} q={q} categories={categories} onAnswer={(a) => answer(q.id, a)} onReceipt={(f) => receipt(q.id, f)} />
        ))}
      </ul>
    </>
  );
}

// --- Signed-in portal ------------------------------------------------------------------

type PL = { income: number; cogs: number | null; grossProfit: number | null; expenses: number; netIncome: number; addsUp: boolean };
type Figures = {
  period: { label: string; start: string; end: string };
  month: PL;
  ytd: PL & { start: string };
  cash: number | null;
  receivables: number;
  payables: number;
};
type BooksCard = {
  clientId: string;
  figuresHidden: boolean;
  booksClosedThrough: string | null;
  openQuestions: number;
  nextDeadline: { name: string; dueDate: string | null } | null;
  statements: Array<{ id: string; filename: string; createdAt: string }>;
  figures: Figures | null;
  note: string | null;
};

/** Accounting style: negatives in parentheses. */
const money = (c: number) => (c < 0 ? `(${formatCents(-c)})` : formatCents(c));

function Figure({ label, value, hint }: { label: string; value: number | null; hint?: string }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold ${value !== null && value < 0 ? "text-destructive" : ""}`}>{value === null ? "—" : money(value)}</p>
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function PortalBooksPage({ clientName }: { clientName: (id: string) => string | undefined }) {
  const { data, isLoading } = useQuery<BooksCard[]>({ queryKey: ["/api/portal/books/dashboard"] });
  const qs = useQuery<Array<{ clientId: string; questions: ClientQuestion[]; categories: Categories }>>({ queryKey: ["/api/portal/books/questions"] });
  const qc = useQueryClient();
  if (isLoading || !data) return <Loading />;
  const many = data.length > 1;
  const answer = (id: string, a: Answer) => api.send("POST", `/api/portal/books/questions/${id}/answer`, a);
  const receipt = async (id: string, f: File) => {
    await api.upload(`/api/portal/books/questions/${id}/receipt`, {}, f);
    await qc.invalidateQueries({ queryKey: ["/api/portal/books/questions"] });
  };
  return (
    <>
      <PageHeader title="Financial Reports" description="Prepared from QuickBooks after each month-end close." />
      {!data.length && <Empty>You are not currently engaged for accounting services.</Empty>}
      <div className="space-y-6">
        {data.map((c) => {
          const f = c.figures;
          const myQs = qs.data?.find((x) => x.clientId === c.clientId);
          const ytdLabel = f ? `Year to date (from ${formatDate(f.ytd.start)}):` : "";
          return (
            <div key={c.clientId} className="space-y-4">
              {many && <h2 className="text-lg font-semibold">{clientName(c.clientId)}</h2>}
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                {!c.figuresHidden && (
                  <span>
                    Books closed through: <strong>{c.booksClosedThrough ? formatDate(c.booksClosedThrough) : "not yet"}</strong>
                  </span>
                )}
                {c.nextDeadline && (
                  <span className="text-muted-foreground">
                    Next: {c.nextDeadline.name}
                    {c.nextDeadline.dueDate && ` (by ${formatDate(c.nextDeadline.dueDate)})`}
                  </span>
                )}
              </div>
              {f ? (
                <Section title={`${f.period.label} at a glance`}>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Figure label="Revenue" value={f.month.income} hint={`${ytdLabel} ${money(f.ytd.income)}`} />
                    {f.month.cogs !== null && <Figure label="Cost of goods sold" value={f.month.cogs} hint={`${ytdLabel} ${money(f.ytd.cogs ?? 0)}`} />}
                    <Figure label="Operating expenses" value={f.month.expenses} hint={`${ytdLabel} ${money(f.ytd.expenses)}`} />
                    <Figure label="Net income" value={f.month.netIncome} hint={`${ytdLabel} ${money(f.ytd.netIncome)}`} />
                    <Figure label="Cash balance" value={f.cash} hint={`As of ${formatDate(f.period.end)}`} />
                    <Figure label="Accounts receivable" value={f.receivables} />
                    <Figure label="Accounts payable" value={f.payables} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Accrual basis, as reported by QuickBooks for the closed period.
                    {!(f.month.addsUp && f.ytd.addsUp) && " Net income also includes other income and expenses (see your full report)."}
                  </p>
                </Section>
              ) : (
                c.note && <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">{c.note}</p>
              )}
              {myQs && myQs.questions.length > 0 && (
                <Section title="Transactions for your review">
                  <QuestionList questions={myQs.questions} categories={myQs.categories} answer={answer} receipt={receipt} />
                </Section>
              )}
              <PortalBills clientId={c.clientId} />
              <Section title="Financial statements">
                {!c.statements.length ? (
                  <Empty>Monthly financial statements will appear here after each month-end close.</Empty>
                ) : (
                  <ul className="divide-y divide-border">
                    {c.statements.map((s) => (
                      <li key={s.id} className="flex items-center gap-3 py-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <a href={`/api/portal/documents/${s.id}/download`} className="flex-1 text-sm hover:underline">
                          {s.filename}
                        </a>
                        <span className="text-xs text-muted-foreground">{formatDate(s.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          );
        })}
      </div>
    </>
  );
}

type SentBill = { id: string; vendorName: string; totalCents: number | null; status: "needs_review" | "posted" | "rejected"; createdAt: string };

/** The client sends bills to the firm; they're read and recorded in QuickBooks with the bill attached. */
function PortalBills({ clientId }: { clientId: string }) {
  const { toast } = useToast();
  const key = [`/api/portal/bills?clientId=${clientId}`];
  const { data } = useQuery<SentBill[]>({ queryKey: key });
  const send = useMutation({
    meta: { inlineError: true },
    mutationFn: (f: File) => api.upload("/api/portal/bills", { clientId }, f),
    onSuccess: () => toast({ title: "Bill sent", description: "Your accountant will record it." }),
  });
  const label: Record<SentBill["status"], string> = { needs_review: "With your accountant", posted: "Recorded", rejected: "Not recorded" };
  return (
    <Section title="Bills" description="Send bills you receive (PDF or photo). They're read and recorded in your books with the bill attached." action={<UploadButton label={send.isPending ? "Sending…" : "Send a bill"} disabled={send.isPending} onFile={(f) => send.mutate(f)} />}>
      {send.error && <p className="mb-2 text-sm text-destructive">{errorMessage(send.error)}</p>}
      {!data?.length ? (
        <Empty>No bills sent yet.</Empty>
      ) : (
        <ul className="divide-y divide-border">
          {data.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{b.vendorName || "Bill"}</span>
              {b.totalCents !== null && <span className="tabular">{money(b.totalCents)}</span>}
              <span className={b.status === "posted" ? "text-xs text-emerald-700 dark:text-emerald-300" : "text-xs text-muted-foreground"}>{label[b.status]}</span>
              <span className="text-xs text-muted-foreground">{formatDate(b.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Home-page nudge when transaction questions are waiting. */
export function BooksQuestionsCard() {
  // The questions list is a plain database read; the dashboard would also ask QuickBooks for reports.
  const { data } = useQuery<Array<{ questions: ClientQuestion[] }>>({ queryKey: ["/api/portal/books/questions"] });
  const open = (data ?? []).reduce((n, c) => n + c.questions.filter((q) => q.status === "open").length, 0);
  if (!open) return null;
  return (
    <Link href="/books" className="mb-3 flex items-center gap-3 rounded-xl border border-card-border bg-card p-4 shadow-xs hover:border-primary/50">
      <HelpCircle className="h-5 w-5 text-primary" />
      <span className="flex-1">
        <span className="block font-medium">
          {open} transaction{open > 1 ? "s require" : " requires"} your review
        </span>
        <span className="text-sm text-muted-foreground">Please describe each transaction so it can be categorized.</span>
      </span>
    </Link>
  );
}

// --- No-sign-in link (/q#token) -------------------------------------------------------------

type LinkData = { firmName: string; clientName: string; expiresAt: string; questions: ClientQuestion[]; categories: Categories };

export function QuestionLinkPage() {
  // The token lives in the URL fragment, which browsers never send to servers or proxies.
  const [token] = useState(() => window.location.hash.slice(1));
  const headers = { "x-link-token": token, "x-clientflow-csrf": "1" };
  const call = async <T,>(url: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(url, { ...init, credentials: "omit", headers: { ...headers, ...(init?.headers ?? {}) } });
    const body = res.headers.get("content-type")?.includes("application/json") ? await res.json() : {};
    if (!res.ok) throw new ApiError(res.status, body.error?.code ?? "error", body.error?.message ?? "The request could not be completed. Please try again.", body.error?.details);
    return body as T;
  };
  const { data, isLoading, error, refetch } = useQuery<LinkData>({ queryKey: ["q-link", token], queryFn: () => call<LinkData>("/api/q"), enabled: token.length >= 20, retry: false, staleTime: Infinity });
  const answer = async (id: string, a: Answer) => {
    await call(`/api/q/questions/${id}/answer`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(a) });
    await refetch();
  };
  const receipt = async (id: string, f: File) => {
    const form = new FormData();
    form.append("file", f);
    await call(`/api/q/questions/${id}/receipt`, { method: "POST", body: form });
    await refetch();
  };
  return (
    <main className="mx-auto min-h-screen max-w-2xl px-4 py-8">
      {!data && <h1 className="sr-only">Questions about your transactions</h1>}
      {token.length < 20 ? (
        <Empty>This link is incomplete. Please open it directly from the original email.</Empty>
      ) : isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{error instanceof ApiError && error.status === 429 ? "Too many attempts. Please wait a few minutes and try again." : errorMessage(error)}</Empty>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{data.firmName}</p>
          <h1 className="mb-1 text-2xl font-semibold">Questions about {data.clientName}'s transactions</h1>
          <p className="mb-6 text-xs text-muted-foreground">This private link works until {formatDate(data.expiresAt)}. Please don't forward it.</p>
          {!data.questions.length ? <Empty>No transactions require your review.</Empty> : <QuestionList questions={data.questions} categories={data.categories} answer={answer} receipt={receipt} />}
        </>
      )}
    </main>
  );
}
